import { transition } from '@helpers/animate';
import brand from '@helpers/brand';
import { Device, from, until } from '@helpers/media';
import ControlsStyles from '@stories/Components/Misc/Carousels/Carousel/Controls/Controls.styles';
import { DotsStyles } from '@stories/Components/Misc/Carousels/Carousel/Dots/Dots.styles';
import MotionTrack from '@stories/Components/Misc/Carousels/Carousel/MotionTrack/MotionTrack';
import PaginationStyles from '@stories/Components/Misc/Carousels/Carousel/Pagination/Pagination.styles';
import HeadingSummaryBlockStyles from '@stories/Components/Misc/HeadingSummaryBlock/HeadingSummaryBlock.styles';
import SectionWrapper, { BackgroundVariant, } from '@stories/Components/Misc/SectionWrapper/SectionWrapper';
import SectionWrapperStyles from '@stories/Components/Misc/SectionWrapper/SectionWrapper.styles';
import styled from 'styled-components';
const Container = styled(SectionWrapper) `
  --highlightColour: ${brand.primary.blue.light};

  position: relative;
  overflow: hidden;

  &[data-variant=${BackgroundVariant.Blue}],
  &[data-variant=${BackgroundVariant.PurpleDark}] {
    --boxColour: ${brand.primary.purple.light};
  }

  &[data-variant=${BackgroundVariant.Purple}] {
    --boxColour: ${brand.primary.purple.dark};
  }

  &[data-variant=${BackgroundVariant.Blue}][data-background-box='false'] {
    ${ControlsStyles.Container} {
      --buttonShadowColour: ${brand.global.black};
    }
  }

  ${HeadingSummaryBlockStyles.Container} {
    margin-top: auto;
    position: relative;
    z-index: 2;
  }

  ${HeadingSummaryBlockStyles.Heading},
  ${HeadingSummaryBlockStyles.Subheading},
  ${HeadingSummaryBlockStyles.Content}, 
  ${HeadingSummaryBlockStyles.ButtonWrapper},
  ${PaginationStyles.Container} {
    ${transition('opacity, transform')};
  }

  ${HeadingSummaryBlockStyles.Subheading} {
    transition-delay: 300ms;
  }

  ${HeadingSummaryBlockStyles.Heading} {
    transition-delay: 450ms;

    > span {
      transition-delay: 850ms;
    }
  }

  ${HeadingSummaryBlockStyles.Content} {
    transition-delay: 600ms;
  }

  ${HeadingSummaryBlockStyles.ButtonWrapper} {
    transition-delay: 750ms;
  }

  ${PaginationStyles.Container} {
    transition-delay: 1000ms;
  }

  ${SectionWrapperStyles.Inner} {
    display: flex;
    flex-direction: column;
    gap: 32px;
  }

  ${ControlsStyles.Container} {
    --buttonShadowColour: ${brand.primary.purple.dark};
  }

  &[data-animate='false'] {
    ${HeadingSummaryBlockStyles.Heading},
    ${HeadingSummaryBlockStyles.Subheading},
    ${HeadingSummaryBlockStyles.Content}, 
    ${HeadingSummaryBlockStyles.ButtonWrapper} {
      opacity: 0;
      transform: translateY(20px);
    }

    ${PaginationStyles.Container} {
      opacity: 0;
      transform: translateY(-20px);
    }
  }

  @media ${until(Device.TabletLarge)} {
    &[data-variant=${BackgroundVariant.Blue}] ${ControlsStyles.Container} {
      --buttonShadowColour: ${brand.global.black};
    }
  }

  @media ${from(Device.TabletLarge)} {
    &[data-background-box='false']::before,
    &[data-background-box='false'] ${SectionWrapperStyles.Inner}::before {
      display: none;
    }

    &::before,
    ${SectionWrapperStyles.Inner}::before, &::after {
      content: '';
      position: absolute;
      left: 0;
    }

    &::before {
      top: 0;
      height: 100%;
      width: calc((100vw - var(--siteWidth)) * 0.5);
      background-color: var(--boxColour);
      z-index: 2;
    }

    &::after {
      --additional: 6px;

      top: 0;
      z-index: 1;
      height: 100%;
      background-color: var(--sectionBackground);
      width: calc(
        ((100vw - var(--siteWidth)) * 0.5) +
          ((var(--siteWidth) + var(--sitePadding) + var(--sitePadding)) * 0.25 + var(--additional))
      );

      @media ${from(Device.DesktopLarge)} {
        --additional: -16px;
      }
    }

    ${SectionWrapperStyles.Inner} {
      display: grid;
      position: relative;
      grid-template-columns: repeat(4, 1fr);

      &::before {
        ${transition('transform')};

        top: 50%;
        width: 600px;
        aspect-ratio: 1;
        border-bottom-right-radius: 90px;
        background-color: var(--boxColour);
        transform: translate(-50%, -40%) rotate(-25deg);
        z-index: 2;
      }
    }

    &[data-animate='false'] ${SectionWrapperStyles.Inner}::before {
      transform: translate(-50%, -45%);
    }

    ${HeadingSummaryBlockStyles.Container} {
      grid-row: 1 / span 1;
      grid-column: 1 / span 1;
    }

    ${PaginationStyles.Container} {
      z-index: 2;
      grid-row: 2 / span 1;
      grid-column: 1 / span 1;
      flex-direction: row-reverse;
      justify-content: flex-end;
      margin-bottom: auto;
    }

    ${DotsStyles.Dots} {
      max-width: 64px;
    }
  }
`;
const CarouselWrapper = styled.div `
  ${transition('opacity, transform', 0.5)};

  --itemPeek: 16px;
  --itemGutter: var(--gutterWidth);
  --visibleItems: 1;

  z-index: 1;
  margin: 0 calc(var(--itemGutter) * -0.5);
  transition-delay: 650ms;

  @media ${from(Device.TabletSmall)} {
    --visibleItems: 2;
  }

  @media ${from(Device.TabletLarge)} {
    grid-row: 1 / span 2;
    grid-column: 2 / span 3;
  }

  @media ${from(Device.Desktop)} {
    --itemPeek: 135px;
  }

  ${Container}[data-animate='false'] & {
    opacity: 0;
    transform: translateX(25%);
  }
`;
const Track = styled(MotionTrack) ``;
const Item = styled.li `
  display: block;
  flex: 0 0 calc(100% / var(--visibleItems) - (var(--itemPeek, 0px) / var(--visibleItems)));
  padding: 0 calc(var(--itemGutter) / 2);

  &[data-is-featured='true'] {
    @media ${from(Device.TabletLarge)} {
      flex: 0 0 calc(200% / var(--visibleItems) - (var(--itemPeek, 0px) / var(--visibleItems)));
    }
  }
`;
const TestimonialCarouselStyles = {
    Container,
    CarouselWrapper,
    Track,
    Item,
};
export default TestimonialCarouselStyles;
